import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Message } from "./types";

type ChatMessageListProps = {
  messages: Message[];
  messageStep: number;
  assistantReplyStreaming?: boolean;
};

const GreetingMessage: Message = {
  role: "assistant",
  text: "Hi, how can I help you today?",
  timeStamp: null,
};

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  messageStep,
  assistantReplyStreaming = false,
}) => {
  const [lastResponseRender, setLastResponseRender] = useState<Message | null>(
    null
  );
  const [revealedLength, setRevealedLength] = useState(0);
  const revealedRef = useRef(0);

  useEffect(() => {
    revealedRef.current = revealedLength;
  }, [revealedLength]);

  const lastAssistantText =
    messages.length > 0 && messages[messages.length - 1].role === "assistant"
      ? messages[messages.length - 1].text
      : null;

  /* Complete message / non-streaming */
  useLayoutEffect(() => {
    if (messageStep === 0) {
      setLastResponseRender(null);
      return;
    }

    const last = messages[messages.length - 1];
    if (last == null || last.role !== "assistant") {
      return;
    }

    setLastResponseRender(last);
    setRevealedLength(0);
  }, [messageStep]);

  useEffect(() => {
    if (messageStep === 0) {
      return;
    }

    const last = messages[messages.length - 1];
    if (last == null || last.role !== "assistant") {
      return;
    }

    const words = last.text.split(" ");
    let cancelled = false;

    const animationStep = (revealed: number): void => {
      if (cancelled) {
        return;
      }
      setRevealedLength(revealed);

      if (revealed >= words.length) {
        return;
      }

      window.setTimeout(() => animationStep(revealed + 1), 12);
    };

    animationStep(0);

    return () => {
      cancelled = true;
    };
  }, [messageStep]);

  /* Streaming */
  useEffect(() => {
    if (!assistantReplyStreaming || lastAssistantText == null) {
      return;
    }

    const words = lastAssistantText.split(" ");
    const target = words.length;
    let cancelled = false;

    const animationStep = (revealed: number): void => {
      if (cancelled) {
        return;
      }
      setRevealedLength(revealed);

      if (revealed >= target) {
        return;
      }

      window.setTimeout(() => animationStep(revealed + 1), 12);
    };

    const start = Math.min(revealedRef.current, target);
    animationStep(start);

    return () => {
      cancelled = true;
    };
  }, [assistantReplyStreaming, lastAssistantText]);

  const renderAnimatedResponse = (
    message: Message,
    index: number,
    revealedWordCount: number
  ): React.JSX.Element => {
    const words = message.text.split(" ");
    let revealedText = "";

    for (let i = 0; i < Math.min(revealedWordCount, words.length); i++) {
      revealedText += words[i];
      if (i !== words.length - 1) {
        revealedText += " ";
      }
    }

    const hiddenText = message.text.slice(
      Math.min(revealedText.length, message.text.length - 1),
      message.text.length - 1
    );

    return (
      <li
        key={index}
        className="tw-w-full tw-flex tw-flex-col tw-gap-2 tw-mb-4 tw-items-start"
      >
        <div className="tw-w-fit tw-max-w-xl tw-p-4 tw-rounded">
          <div className="tw-font-semibold">Assistant</div>
          <p>
            {revealedWordCount !== undefined ? (
              <>
                <span>{revealedText}</span>
                <span className="tw-text-transparent tw-select-none">
                  {hiddenText}
                </span>
              </>
            ) : (
              message.text
            )}
          </p>
          {message.timeStamp && (
            <time className="tw-text-xs tw-mt-2 tw-text-gray-500">
              {new Date(message.timeStamp).toLocaleString()}
            </time>
          )}
        </div>
        {message.imageURL && (
          <img
            className="tw-rounded-md tw-border-solid tw-border"
            src={message.imageURL}
          />
        )}
      </li>
    );
  };

  const renderChatMessage = (
    message: Message,
    index: number
  ): React.JSX.Element => {
    return (
      <li
        key={index}
        className={`tw-w-full tw-flex tw-flex-col tw-gap-2 tw-mb-4 ${
          message.role == "assistant" ? "tw-items-start" : "tw-items-end"
        }`}
      >
        <div
          className={`tw-w-fit tw-max-w-xl tw-p-4 tw-rounded ${
            message.role === "user" ? "tw-bg-gray-200" : ""
          }`}
        >
          {message.role === "assistant" && (
            <div className="tw-font-semibold">Assistant</div>
          )}

          <p>{message.text}</p>
          {message.timeStamp && (
            <time
              className={`tw-text-xs tw-mt-2 tw-text-gray-500 ${
                message.role === "user" ? "tw-text-right" : ""
              }`}
            >
              {new Date(message.timeStamp).toLocaleString()}
            </time>
          )}
        </div>
        {message.imageURL && (
          <img
            className="tw-rounded-md tw-border-solid tw-border"
            src={message.imageURL}
          />
        )}
      </li>
    );
  };

  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;

  const animateCompleteResponse =
    lastMessage != null &&
    lastMessage.role === "assistant" &&
    messageStep > 0 &&
    lastResponseRender != null &&
    lastMessage.text === lastResponseRender.text;

  const animateStreamedAssistant =
    assistantReplyStreaming &&
    lastMessage != null &&
    lastMessage.role === "assistant";

  const useAnimatedLastMessage =
    animateCompleteResponse || animateStreamedAssistant;

  return (
    <ul>
      {messages.length > 0 ? (
        <>
          {messages
            .slice(0, -1)
            .map((message, index) => renderChatMessage(message, index))}
          {lastMessage != null &&
            (useAnimatedLastMessage
              ? renderAnimatedResponse(
                  lastMessage,
                  messages.length - 1,
                  revealedLength
                )
              : renderChatMessage(lastMessage, messages.length - 1))}
        </>
      ) : (
        renderChatMessage(GreetingMessage, 0)
      )}
    </ul>
  );
};
